<template>
  <v-container class="service-status-container">
    <v-card class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2">mdi-heart-pulse</v-icon>
        System-Status
        <v-spacer></v-spacer>
        <v-chip :color="overallStatus.color" size="small">{{
          overallStatus.text
        }}</v-chip>
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-6">
          Diese Seite zeigt den aktuellen Status und die Verfügbarkeit aller
          ARCM-Dienste.
        </p>

        <v-row>
          <v-col cols="12" sm="4" v-for="(service, key) in services" :key="key">
            <v-card
              class="service-card"
              :color="getStatusBackground(service.status)"
            >
              <v-card-title class="py-2 px-4 d-flex align-center">
                <v-icon :color="getStatusColor(service.status)" class="mr-2">{{
                  service.icon
                }}</v-icon>
                {{ service.name }}
                <v-spacer></v-spacer>
                <v-chip
                  :color="getStatusColor(service.status)"
                  size="x-small"
                  >{{ getStatusText(service.status) }}</v-chip
                >
              </v-card-title>

              <v-card-text class="py-2 px-4">
                <v-progress-linear
                  :model-value="service.uptime"
                  :color="getStatusColor(service.status)"
                  height="8"
                  rounded
                ></v-progress-linear>
                <div class="d-flex justify-space-between align-center mt-2">
                  <span class="text-caption">Uptime (30 Tage)</span>
                  <span class="text-caption font-weight-bold"
                    >{{ service.uptime }}%</span
                  >
                </div>
                <div class="mt-2 text-caption" v-if="service.message">
                  <v-icon size="12" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  {{ service.message }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="status-history-card">
      <v-card-title>
        <v-icon color="primary" class="mr-2">mdi-history</v-icon>
        Status-Historie
      </v-card-title>

      <v-card-text>
        <div v-if="incidents.length === 0" class="text-center py-4">
          <p class="text-body-1">Keine Vorfälle in den letzten 30 Tagen 🎉</p>
        </div>

        <v-timeline v-else side="end" density="compact">
          <v-timeline-item
            v-for="(incident, index) in incidents"
            :key="index"
            :dot-color="getIncidentColor(incident.status)"
            size="small"
          >
            <div class="d-flex align-center mb-1">
              <v-chip
                size="small"
                :color="getIncidentColor(incident.status)"
                class="mr-2"
              >
                {{ incident.service }}
              </v-chip>
              <span class="text-caption text-medium-emphasis">{{
                incident.date
              }}</span>
            </div>
            <div class="incident-message">{{ incident.message }}</div>
            <div
              class="text-caption text-medium-emphasis"
              v-if="incident.resolution"
            >
              <v-icon size="12" class="mr-1">mdi-check-circle</v-icon>
              {{ incident.resolution }}
            </div>
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>

    <div class="text-center mt-8">
      <p class="text-caption text-medium-emphasis">
        Letzte Aktualisierung: {{ lastUpdated }}
        <v-btn
          icon="mdi-refresh"
          variant="text"
          size="small"
          @click="fetchServiceStatus"
          :loading="loading"
        ></v-btn>
      </p>
    </div>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import axios from "axios";
import { API_BASE_URL } from "@/config";

const loading = ref(false);
const refreshInterval = ref(null);
const lastUpdated = ref(new Date().toLocaleString());

// Services & Status
const services = ref({
  web: {
    name: "Webdienst",
    status: "operational",
    uptime: 99.97,
    icon: "mdi-web",
    message: null,
  },
  database: {
    name: "Datenbank",
    status: "operational",
    uptime: 99.99,
    icon: "mdi-database",
    message: null,
  },
  websocket: {
    name: "WebSocket",
    status: "operational",
    uptime: 99.8,
    icon: "mdi-webhook",
    message: null,
  },
});

// Vorfälle für die Historie
const incidents = ref([]);

// Gesamtstatus berechnen
const overallStatus = computed(() => {
  const statuses = Object.values(services.value).map((s) => s.status);

  if (statuses.includes("outage")) {
    return { text: "Systemstörung", color: "error" };
  }

  if (statuses.includes("degraded")) {
    return { text: "Beeinträchtigter Betrieb", color: "warning" };
  }

  if (statuses.includes("maintenance")) {
    return { text: "Wartung", color: "info" };
  }

  return { text: "Alle Systeme funktionieren", color: "success" };
});

// Hilfsfunktionen
const getStatusColor = (status) => {
  switch (status) {
    case "operational":
      return "success";
    case "degraded":
      return "warning";
    case "maintenance":
      return "info";
    case "outage":
      return "error";
    default:
      return "grey";
  }
};

const getStatusBackground = (status) => {
  switch (status) {
    case "operational":
      return "success-lighten-5";
    case "degraded":
      return "warning-lighten-5";
    case "maintenance":
      return "info-lighten-5";
    case "outage":
      return "error-lighten-5";
    default:
      return "";
  }
};

const getStatusText = (status) => {
  switch (status) {
    case "operational":
      return "Aktiv";
    case "degraded":
      return "Eingeschränkt";
    case "maintenance":
      return "Wartung";
    case "outage":
      return "Störung";
    default:
      return "Unbekannt";
  }
};

const getIncidentColor = (status) => {
  switch (status) {
    case "resolved":
      return "success";
    case "ongoing":
      return "error";
    case "investigating":
      return "warning";
    default:
      return "info";
  }
};

// Status von der API abrufen
const fetchServiceStatus = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}status.php`);

    if (response.data && response.data.status === "success") {
      // Services aktualisieren
      if (response.data.services) {
        services.value = response.data.services;
      }

      // Vorfälle aktualisieren
      if (response.data.incidents) {
        incidents.value = response.data.incidents;
      }

      lastUpdated.value = new Date().toLocaleString();
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Status-Daten:", error);
  } finally {
    loading.value = false;
  }
};

// Lifecycle hooks
onMounted(() => {
  fetchServiceStatus();

  // Automatische Aktualisierung alle 60 Sekunden
  refreshInterval.value = setInterval(fetchServiceStatus, 60000);
});

onUnmounted(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }
});
</script>

<style scoped>
.service-status-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

.status-overview-card,
.status-history-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.service-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px;
  height: 100%;
}

.service-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.incident-message {
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.text-caption {
  font-size: 0.75rem;
}

/* Dark mode anpassungen */
:deep(.v-timeline-item__body) {
  color: rgba(255, 255, 255, 0.9);
}

:deep(.v-timeline-divider__dot) {
  background: transparent !important;
}
</style>
